import { useStakingAPY, type StakingPositionStore, useDialogPortal } from "@/hooks";
import { memo, useMemo, type MouseEvent, useCallback } from "react";
import { usePortfolioPositionRow } from "../usePortfolioPositionRow";
import { PositionTableRow } from "../row/PositionTableRow";
import type { DepositMoreFormDialogProps } from "@/components";
import { PositionStatus } from "../row/position-table-row.utils";
import { usePortfolioStore } from "@/zustand/PortfolioStore";
import { formatDisplayCurrency } from "@/utils/format-currency";

export interface StakedPositionRowProps {
    position: StakingPositionStore;
    index: number;
    isMenuOpen: boolean;
    menuIndex: number | null;
    onMenuToggle: (
        event: MouseEvent<HTMLElement>,
        position: StakingPositionStore,
        index: number,
        status?: PositionStatus,
    ) => void;
}

export const StakedPositionRow = memo(function StakedPositionRow({
    position,
    index,
    isMenuOpen,
    menuIndex,
    onMenuToggle,
}: StakedPositionRowProps) {
    const { durationText } = usePortfolioPositionRow({
        position,
        positionType: "staked",
    });

    const { selectedPosition, setSelectedPosition } = usePortfolioStore();
    const rowKey = `staked-${index}`;
    const isSelected = selectedPosition?.key === rowKey;
    const handleSelect = useCallback(() => {
        setSelectedPosition({ type: "staked", key: rowKey, position });
    }, [position, rowKey, setSelectedPosition]);

    const { openDialog } = useDialogPortal();

    const stakedBalance = useMemo(() => Number(position.stakedBalance), [position.stakedBalance]);

    const handleAction = useCallback(() => {
        openDialog({
            dialogType: "DEPOSIT_MORE_FORM",
            dialogProps: {
                existingStakeAmount: stakedBalance,
            } as DepositMoreFormDialogProps,
        });
    }, [openDialog, stakedBalance]);

    const { apy, isAPYLoading } = useStakingAPY(stakedBalance);

    const {
        principal,
        rewards,
        apy: apyText,
    } = useMemo(() => {
        const principalValue = Number(position.stakedBalance);
        const rewardsValue = Number(position.rewardsEarned);

        return {
            principal: `${formatDisplayCurrency(principalValue)} iUSD`,
            rewards: `${formatDisplayCurrency(rewardsValue)}`,
            apy: isAPYLoading ? "—" : apy ? `${apy}%` : "N/A",
        };
    }, [position.stakedBalance, position.rewardsEarned, apy, isAPYLoading]);

    return (
        <PositionTableRow
            durationText={durationText}
            principal={principal}
            rewards={rewards}
            apy={apyText}
            isActionDisabled={false}
            handleAction={handleAction}
            actionButtonText="Stake More"
            isMenuOpen={isMenuOpen}
            menuIndex={menuIndex}
            index={index}
            onMenuToggle={(event) => onMenuToggle(event, position, index, PositionStatus.EARNING)}
            isSelected={isSelected}
            onSelect={handleSelect}
            status={PositionStatus.EARNING}
        />
    );
});

StakedPositionRow.displayName = "StakedPositionRow";
